import type {
  GameplayLifecycle,
  GameplayLifecycleSnapshot,
} from "../application/gameplay-lifecycle";
import { DialogFocus } from "./dialog-focus";
import { SoundtrackSelect } from "./soundtrack-select";

export interface PauseShellElements {
  readonly homeButton: HTMLButtonElement;
  readonly modeScreen: HTMLElement;
  readonly musicButton: HTMLButtonElement;
  readonly pauseButton: HTMLButtonElement;
  readonly pauseOverlay: HTMLElement;
  readonly restartButton: HTMLButtonElement;
  readonly resumeButton: HTMLButtonElement;
  readonly soundButton: HTMLButtonElement;
  readonly soundtrackSelect: HTMLSelectElement;
}

const requireElement = (documentScope: Document, id: string): HTMLElement => {
  const element = documentScope.getElementById(id);
  if (element === null) {
    throw new Error(`Missing pause shell element: ${id}`);
  }
  return element;
};

export class PauseShell {
  private readonly soundtrackSelect: SoundtrackSelect;
  private readonly unsubscribeLifecycle: () => void;

  public constructor(
    private readonly lifecycle: GameplayLifecycle,
    private readonly elements: PauseShellElements,
    private readonly dialogFocus: DialogFocus,
  ) {
    elements.pauseButton.addEventListener("click", this.handlePause);
    elements.resumeButton.addEventListener("click", this.handleResume);
    elements.restartButton.addEventListener("click", this.handleRestart);
    elements.homeButton.addEventListener("click", this.handleHome);
    elements.musicButton.addEventListener("click", this.handleMusic);
    elements.soundButton.addEventListener("click", this.handleSound);
    this.soundtrackSelect = new SoundtrackSelect(
      elements.soundtrackSelect,
      lifecycle.getSnapshot().selectedSoundtrackId,
      (id) => lifecycle.selectSoundtrack(id),
    );
    elements.pauseOverlay.addEventListener(
      "pointerdown",
      this.handleOverlayPointer,
    );
    this.unsubscribeLifecycle = lifecycle.subscribe((snapshot) => {
      this.render(snapshot);
    });
  }

  public destroy(): void {
    this.elements.pauseButton.removeEventListener("click", this.handlePause);
    this.elements.resumeButton.removeEventListener("click", this.handleResume);
    this.elements.restartButton.removeEventListener(
      "click",
      this.handleRestart,
    );
    this.elements.homeButton.removeEventListener("click", this.handleHome);
    this.elements.musicButton.removeEventListener("click", this.handleMusic);
    this.elements.soundButton.removeEventListener("click", this.handleSound);
    this.soundtrackSelect.destroy();
    this.elements.pauseOverlay.removeEventListener(
      "pointerdown",
      this.handleOverlayPointer,
    );
    this.dialogFocus.destroy();
    this.unsubscribeLifecycle();
  }

  private readonly handleHome = (): void => {
    this.lifecycle.showHome();
  };

  private readonly handleMusic = (): void => {
    void this.lifecycle.toggleMusic();
  };

  private readonly handleOverlayPointer = (event: Event): void => {
    if (event.target === event.currentTarget) {
      event.stopPropagation();
    }
  };

  private readonly handlePause = (): void => {
    this.lifecycle.pause();
  };

  private readonly handleRestart = (): void => {
    this.lifecycle.restart();
  };

  private readonly handleResume = (): void => {
    this.lifecycle.resume();
  };

  private readonly handleSound = (): void => {
    void this.lifecycle.toggleSound();
  };

  private render(snapshot: GameplayLifecycleSnapshot): void {
    const gameplayVisible = snapshot.scene === "gameplay";
    const pauseVisible = gameplayVisible && snapshot.paused;
    this.elements.pauseButton.hidden = !gameplayVisible;
    this.elements.pauseOverlay.hidden = !pauseVisible;
    if (pauseVisible) {
      this.elements.modeScreen.setAttribute("data-paused", "true");
      this.dialogFocus.activate();
    } else {
      this.elements.modeScreen.removeAttribute("data-paused");
      this.dialogFocus.deactivate();
    }
    this.renderPreference(
      this.elements.musicButton,
      "Music",
      snapshot.preferences.musicEnabled,
      snapshot.progressPersistence,
    );
    this.renderPreference(
      this.elements.soundButton,
      "Sound",
      snapshot.preferences.soundEnabled,
      snapshot.progressPersistence,
    );
    this.elements.soundtrackSelect.disabled =
      snapshot.progressPersistence === "pending";
    this.soundtrackSelect.render(snapshot.selectedSoundtrackId);
  }

  private renderPreference(
    button: HTMLButtonElement,
    label: "Music" | "Sound",
    enabled: boolean,
    persistence: GameplayLifecycleSnapshot["progressPersistence"],
  ): void {
    button.disabled = persistence === "pending";
    button.setAttribute("aria-pressed", String(enabled));
    const text =
      persistence === "pending"
        ? `${label} saving…`
        : persistence === "failed"
          ? `${label} was not saved. Try again.`
          : `${label} ${enabled ? "on" : "off"}`;
    button.setAttribute("aria-label", text);
    button.textContent = text;
  }
}

export const installPauseShell = (
  documentScope: Document,
  lifecycle: GameplayLifecycle,
): PauseShell => {
  const elements: PauseShellElements = {
    homeButton: requireElement(
      documentScope,
      "pause-home-button",
    ) as HTMLButtonElement,
    modeScreen: requireElement(documentScope, "mode-screen"),
    musicButton: requireElement(
      documentScope,
      "pause-music-button",
    ) as HTMLButtonElement,
    pauseButton: requireElement(
      documentScope,
      "pause-button",
    ) as HTMLButtonElement,
    pauseOverlay: requireElement(documentScope, "pause-overlay"),
    restartButton: requireElement(
      documentScope,
      "pause-restart-button",
    ) as HTMLButtonElement,
    resumeButton: requireElement(
      documentScope,
      "pause-resume-button",
    ) as HTMLButtonElement,
    soundButton: requireElement(
      documentScope,
      "pause-sound-button",
    ) as HTMLButtonElement,
    soundtrackSelect: requireElement(
      documentScope,
      "pause-soundtrack-select",
    ) as HTMLSelectElement,
  };
  return new PauseShell(
    lifecycle,
    elements,
    new DialogFocus(
      documentScope,
      elements.resumeButton,
      [
        elements.resumeButton,
        elements.restartButton,
        elements.homeButton,
        elements.soundtrackSelect,
        elements.musicButton,
        elements.soundButton,
      ],
      elements.pauseButton,
    ),
  );
};
